import {makeAutoObservable, runInAction} from "mobx";
import {IOption} from "../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import {rootContainer, TYPE} from "../../../services/container/root";
import CurrencyService from "../../../services/currency-service";
import {uuid} from "../../../services/helpers";
import {CREDIT_AMOUNT_TYPE} from "../../resolutions/constants";
import CreateRmaSettingsStore from "./create-rma-settings-store";
import OrderProductStore from "./order-product-store";
import RmaRequestStore from "./rma-request-store";
import {RETURN_METHOD_TYPE} from "../../return-methods/return-method-types";
import CreateRmaService, { CREATE_RMA_MODE, ICRmaItemFeeDataResponse, ICRmaItemFeeDataRequest, REQUIRE_CRMA_FOLLOWUP_QUESTIONS } from "../../../services/api/create-rma-service";
import { FEE_TYPE, SHIPPING_LABEL_FEE_TYPES } from "../../policies/policy-types";
import { CHARGE_FEES } from "../../policies/constants";
import CreateRmaFuqStore from "./create-rma-fuq-store";
import OrderStore from "./order-store";



/** This is the interface that this store should eventually build and send to server, note the mandatory fields */
export interface ICreateRmaItemDto {
    /** must-have fields in constructor */
    line_item_id: string;
	quantity: number;
	restocking_fee: number;
	adjustments: number;
    payment_currency_code: string;
	/** we might have few ICreateRmaItemDtos for same line item when properties like RM or resolution or any other property differ
     * hence the need to add unique indentifier which passed to the server for eligibility checks and returns to the client, see group_id on ICreateRmaRequestIsOutOfPolicy */
    group_id: string; 

    /** configurable fields, get their values later */
    /* in god-mode we require resolution and return method
     * in policy-led we require policy */
    resolution_id?: number;
	return_method_id?: number;
    policy_id?: number;
    return_reason_data?: ICreateRmaReturnReasonDataDto;
}

export interface ICreateRmaReturnReasonDataDto {
    id?: number;
    answers?: string;
    follow_up_questions?:ICRmaFollowUpQuestionRequestDto[],
}

export interface ICRmaFollowUpQuestionRequestDto {
    return_reasons_fuq_id: number,
    translation_key: string,
    translation: string,
    answers: ICrmaFollowUpAnswerRequestDto[],
}

export interface ICrmaFollowUpAnswerRequestDto {
    return_reasons_fuq_answer_id: number|null,
    translation_key: string,
    translation: string,
    answer?: string,
}

/** This interface is needed to allow undefined values while editing */
interface ICreateRmaItemDtoChanges extends Partial<ICreateRmaItemDto> {
    group_id: string; 
    quantity: number;
}


class RequestProductStore {
	private readonly _productStore: OrderProductStore;
	private _model: ICreateRmaItemDtoChanges;
	private _editMode: boolean = false;
	private _rmaRequestStore: RmaRequestStore;
    private _createRmaSettings: CreateRmaSettingsStore;
    private _conflictingReturnMethodLabel: string = '';
    private _displayReturnMethodConflicts: boolean = false;
    private _conflictingResolutionLabel: string = '';
    private _displayResolutionConflicts: boolean = false;
    private _conversionRate: number;
    private _isCalculatingFees: boolean = false;
    private _labelFeeCalculatedAmount: number = 0;
    private _returnFeeCalculatedAmount: number = 0;
    private _fuqStoreList: CreateRmaFuqStore[] = [];
    

	constructor(
        orderProductStore: OrderProductStore, 
        rmaRequestStore: RmaRequestStore, 
        createRmaSettingsStore: CreateRmaSettingsStore, 
        model: ICreateRmaItemDto | undefined,
        private _orderStore: OrderStore,
    ) 
    {
		makeAutoObservable(this, {}, {autoBind: true});
		this._rmaRequestStore = rmaRequestStore;
        this._createRmaSettings = createRmaSettingsStore;
        this._conversionRate = this._orderStore.conversionRate;

        if(!model) {
            /** when model was not passes - construct minimum requried model */
            model = {
                line_item_id: orderProductStore.lineItemId!,
                quantity: 1,
                adjustments: 0,
                restocking_fee: 0,
                payment_currency_code: rmaRequestStore.paymentCurrencyCode,
                group_id: uuid(),
                return_reason_data: {}
            };

            /** applying defaults in god-mode */
            if (createRmaSettingsStore.cRmaMode === CREATE_RMA_MODE.C_RMA_GOD_MODE) {
                model = {
                    ...model,
                    return_reason_data: { id: createRmaSettingsStore.merchantReturnReasonId ?? undefined },
                    resolution_id: createRmaSettingsStore.merchantResolutionId ?? undefined,
                    return_method_id: createRmaSettingsStore.returnMethodId ?? undefined,
                    restocking_fee: _formatDecimal((createRmaSettingsStore.restockingFee ?? 0) / this._orderStore.conversionRate),
                    adjustments: _formatDecimal((createRmaSettingsStore.adjustment ?? 0) / this._orderStore.conversionRate),
                };
            }
		}
		else {
            //we got a model from an existing store, so set to edit mode
            this._editMode = true;
		}

		this._model = {
			...model,
		}

        this._productStore = orderProductStore;
        if(model.adjustments === 0 && model.resolution_id && !this.editMode) {
            this._setResolutionAdjustment(model.resolution_id);
        }

        if (model.return_reason_data?.id) {
            this._setFuqStores(model.return_reason_data.id);
        }
	}

    requestModel(): ICreateRmaItemDto {
		return {
            ...this._model,
            return_reason_data: {
                id: this._model.return_reason_data!.id,
                answers: this._getFormattedAnswersString(),
                follow_up_questions: this._fuqStoreList.map(fuqStore => fuqStore.requestModel()),
            },
        } as ICreateRmaItemDto;
	}
    
    private get _currencyService(): CurrencyService { return rootContainer.get<CurrencyService>(TYPE.CurrencyService);}
    private get createRmaService(): CreateRmaService { return rootContainer.get<CreateRmaService>(TYPE.CreateRmaService); }

	get product() { return this._productStore; }
	get uuid(){ return this._model.group_id; }
	get orderProductStore() { return this._productStore; }
	get paymentCurrencyCode(): string|undefined { return this._model?.payment_currency_code; }
    get paymentCurrencySymbol(): string|undefined { return this._currencyService.getCurrencySymbol(this.paymentCurrencyCode!); }
	get quantity(): number { return this._model.quantity; }
	get returnReasonId(): number|undefined { return this._model.return_reason_data?.id; }
	get resolutionId(): number|undefined { return this._model.resolution_id; }
	get returnMethodId(): number|undefined { return this._model.return_method_id; }
	get paidAmountSummary(): number { return this.orderProductStore.paidAmount * this.quantity; }
	get restockingFeeSummary(): number { return (this._model.restocking_fee ?? 0) * this.quantity * -1; }
	get adjustmentsSummary(): number { return (this._model.adjustments ?? 0) * this.quantity; }
	get balanceSummary(): number { return this.paidAmountSummary + this.restockingFeeSummary + this.adjustmentsSummary; }
	get editMode(): boolean { return this._editMode; }
	get totalPaidAmount(): number { return this.getAvailableQuantity() * this.product.paidAmount; }
	get returnMethodLabel(): string { return this._createRmaSettings.getReturnMethodsListOptions().find( i => i.value === this.returnMethodId)?.label; }
	get reasonLabel():string|undefined { return this._createRmaSettings.findReasonOption(this.returnReasonId)?.label; }
	get resolutionLabel(): string {
		for (let i in this._createRmaSettings.getResolutionsListOptions()) {
			const resolution = this._createRmaSettings.getResolutionsListOptions()[i].options.find((resolution: IOption) => resolution.value === this.resolutionId);
			if (resolution) {
				return resolution.label as string;
			}
		}
		return '';
	}
    get conflictingReturnMethod(): string { return this._conflictingReturnMethodLabel; }
	get displayReturnMethodConflicts(): boolean { return this._displayReturnMethodConflicts; }
    get conflictingResolution(): string { return this._conflictingResolutionLabel; }
    get displayResolutionsConflicts(): boolean { return this._displayResolutionConflicts; }
    get policyId(): number|undefined { return this._model.policy_id; }
	get isCalculatingFees(): boolean { return this._isCalculatingFees; }
    get labelFeeCalculatedAmount():number { return this._labelFeeCalculatedAmount; }
    get returnFeeCalculatedAmount():number { return this._returnFeeCalculatedAmount; }
    get compareAtPrice(): number | null { return this._productStore.compareAtPrice(); }
    get fuqStoreList(): CreateRmaFuqStore[] {return this._fuqStoreList; }
    get requireFuq(): boolean { return this._createRmaSettings.requireFuqs === REQUIRE_CRMA_FOLLOWUP_QUESTIONS.MANDATORY; }

	getAvailableQuantity(): number{
		return this.editMode ? this._rmaRequestStore.getRequestInEditMode!.quantity! : this._rmaRequestStore.getOrderProductAvailableQuantity(this.orderProductStore);
	}
	setQuantity(quantity: number) {
        this._model.quantity = quantity;
        this._loadProductFeesAmounts();
    }
	setReturnReason(returnReason: number) { 
        this._model.return_reason_data!.id= returnReason;
        this._setFuqStores(returnReason);
    }
	setReturnMethod(returnMethod: number) { this._model.return_method_id = returnMethod; }
    
	get restockingFee(): number|undefined { return this._model.restocking_fee; }
    setRestockingFee(v: number|null ) {
        if (v === null) {
            /** restocking_fee is required for valid request ICreateRmaItemDto, 
             * but since _model is Partial interface from ICreateRmaItemDto (allows the field to by optional) to allow deleting the value totally from the input while editing the item
             * hence the special case to set undefined when component returns null, typescript doesn't allow to mark the field in partials as nullable when it is not nullable in the origianl interface */
            this._model.restocking_fee = undefined; 
        }
        this._model.restocking_fee = v as number; 
    }
    
	get adjustments(): number|undefined  { return this._model.adjustments; }
    setAdjustment(v: number|null ) { 
        if (v === null) {
            /** same as in setRestockingFee */
            this._model.adjustments = undefined; 
        }
        this._model.adjustments = v as number; 
    }

    setEditMode(editMode: boolean) { this._editMode = editMode; }

    
    /** used by god-mode only from UI */
    setResolution(resolutionId: number) {
        this._model.resolution_id = resolutionId;
        this._setResolutionAdjustment(resolutionId);
    }

    setPolicyId(v: number): void {
        const policyRule = this._createRmaSettings.findPolicyOrFail(v);
        this._model.policy_id = policyRule.id;
        this._model.return_method_id = policyRule.return_method_id;
        this.setResolution(policyRule.resolution_id);
        this._loadProductFeesAmounts();
    }

    private async _loadProductFeesAmounts(): Promise<void> {
        const policyRule = this._createRmaSettings.findPolicyRule(this.policyId);
        if (!policyRule) {
            return;
        }

        const request: ICRmaItemFeeDataRequest = {
            policy_rule_id: policyRule.id,
            catalog_price: this.orderProductStore.catalogPrice,
            paid_price: this.orderProductStore.paidAmount,
            store_currency: this.orderProductStore.storeCurrencyCode,
            payment_currency: this.orderProductStore.paymentCurrencyCode,
            is_order_covered: this._orderStore.isCovered,
        };

        try {
            this._isCalculatingFees = true;
            const res = await this.createRmaService.getCRmaItemFeeDataFromPolicyRule(request);
            runInAction(() => {
                this._setFeesAmounts(res);
                this._isCalculatingFees = false;
                this._rmaRequestStore.recalculateFeesFromPolicyAmounts();
            });
        }
        catch (e) {
            runInAction(() => {
                this._isCalculatingFees=false;
                console.error(e);
            });
        }
    }

    private _setFeesAmounts(feesData: ICRmaItemFeeDataResponse[]) {
        const restockingFee = feesData.find(fee => fee.fee_type.value === FEE_TYPE.RESTOCKING_FEE);
        const labelFee = feesData.find(fee => fee.fee_type.value === null || SHIPPING_LABEL_FEE_TYPES.includes(fee.fee_type.value));
        const returnFee = feesData.find(fee => fee.fee_type.value === FEE_TYPE.RETURN_FEE);

        /** when server did not return fee of certain type - treat as 0, future fees fields should also be resetted here */
        this._model.restocking_fee = 0;
        this._labelFeeCalculatedAmount = 0;
        this._returnFeeCalculatedAmount = 0;

        if (restockingFee) {
            this._model.restocking_fee = restockingFee.amount;
        }

        if (labelFee) {
            this._labelFeeCalculatedAmount = labelFee.amount;
        }
        
        if (returnFee) {
            this._returnFeeCalculatedAmount = returnFee.amount;
        }
    }

    private _setResolutionAdjustment(merchantResolutionId: number){
        const resolution = this._createRmaSettings.findResolution(merchantResolutionId);
        if (!resolution) {
            return;
        }

        if (!resolution.modify_credit_amount || resolution.credit_amount_type === CREDIT_AMOUNT_TYPE.PAID_AMOUNT) {
            this._model.adjustments = 0;
            return;
        }

        if(resolution.credit_amount_type === CREDIT_AMOUNT_TYPE.LESS || resolution.credit_amount_type === CREDIT_AMOUNT_TYPE.MORE){
            const addOrReduce = resolution.credit_amount_type === CREDIT_AMOUNT_TYPE.LESS ? -1 : 1;
            let amount;
            if(resolution.is_percentage){
                amount = this.product.paidAmount * (resolution.modify_credit_amount / 100);
            }
            else {
                amount = resolution.modify_credit_amount / this._conversionRate;
            }
            amount *= addOrReduce;
            this._model.adjustments = amount;
        }
        else if (resolution.credit_amount_type === CREDIT_AMOUNT_TYPE.FIXED){
            this._model.adjustments = (resolution.modify_credit_amount / this._conversionRate) - this.product.paidAmount;
        }
    }   
    
    public getPolicyRulesOptions(): IOption[] { // TODO: when we will support exchange and / or cancel flow- change to GroupedOption[] and present rules according to their catagory 
		const eligiblePolicyRules = this._createRmaSettings.policyRules?.filter( rule => 
            this.orderProductStore.eligiblePolicyRules?.includes(rule.id)) ?? [];

		return eligiblePolicyRules.map((rule) => {
			return {
                value: rule.id,
                label: rule.name
            }
		})
	}


    public getReturnMethodName(): string {
        const returnMethod = this._createRmaSettings.findReturnMethod(this._model.return_method_id);
        return returnMethod?.internal_name ?? "--";
    }

    public getResolutionName(): string {
        const resolution = this._createRmaSettings.findResolution(this._model.resolution_id);
        return resolution?.internal_name ?? "--";
    }

    showReturnMethodConflicts(conflictingReturnMethodLabel: string) {
        this._conflictingReturnMethodLabel = conflictingReturnMethodLabel;
        this._displayReturnMethodConflicts = true;
    }
    hideReturnMethodConflicts() {
        this._displayReturnMethodConflicts = false;
    }
    
    showResolutionConflicts(conflictingResolutionLabel: string) {
        this._conflictingResolutionLabel = conflictingResolutionLabel;
        this._displayResolutionConflicts = true;
    }
    hideResolutionConflicts() {
        this._displayResolutionConflicts = false;
    }

    public isLabelCostSupported(): boolean {
		return this._createRmaSettings.isLabelCostSupported(this.returnMethodId!);
	}

    public hasNoShipmentRequiredRm(): boolean {
        if (!this.returnMethodId) {
            return false;
        }
        return this._createRmaSettings.findReturnMethod(this.returnMethodId)?.type === RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED;
    }

    public isSame(thatStore: RequestProductStore): boolean {
        const thatComp = JSON.stringify({ ...thatStore.requestModel(), quantity: null, group_id: null });
        const thisComp = JSON.stringify({ ...this.requestModel(), quantity: null, group_id: null });

        return thatComp === thisComp;
    }

    public hasWaveAllFeesPolicyRule(): boolean {
        return this._createRmaSettings.findPolicyRule(this._model.policy_id)?.charge_fees === CHARGE_FEES.WAIVE_FEES;
    }

    public isGroupOutOfPolicy(): boolean {
        return this._rmaRequestStore.eligibilityStore.isGroupOutOfPolicy(this._model.group_id);
    }

    private _setFuqStores(reasonId: number) {
        this._fuqStoreList = [];
        const reason = this._createRmaSettings.findReason(reasonId);
        if (!reason) {
            return;
        }

        this._fuqStoreList = reason.follow_up_questions?.map( question => new CreateRmaFuqStore(question)).sort((q1,q2) => (q1.weight > q2.weight) ? 1 : -1) ?? [];
    }

    private _areFuqsAnswered(): boolean {
        if (!this.requireFuq) {
            return true;
        }

        return this._fuqStoreList.every(fuq => fuq.isAnswered);
    }

    public isAddButtonDisabled(isVgroupValidated: boolean): boolean {
        return this.quantity < 1 || !isVgroupValidated || this.isCalculatingFees || !this._areFuqsAnswered();
    }
    private _getFormattedAnswersString() {
        const answers = this._fuqStoreList.map((store) => {
            const selectedAnswers = store.answers.filter(ans => ans.isSelected ?? false);
            if (!selectedAnswers.length) {
                return '';
            }
            return selectedAnswers.map(answer => answer.answer ?? answer.displayName ).join("; ");
        }).filter(Boolean).join("\n> ");

        return `> ${answers}`;
}


}

export default RequestProductStore;

function _formatDecimal(num: number): number {
	return Number(num.toFixed(2));
}
